<template>
    <div>
        <DateRangeFilter v-model="dateRangeFilter" />
        <b-table id="inventory-history-table" :fields="fields" :items="displayHistoryItems"
            empty-text="No Inventory History" sort-direction="desc" sort-by="updated" no-provider-sorting show-empty />
        <BottomPagerBar v-model="pageNumber" :total-rows="filteredHistoryItems.length" :per-page="pageSize"
            :show-page-size-selector="true" :no-stick="true" :show-total="true" @change-page-size="pageSize = $event" />
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import Axios from "axios";
import moment from "moment/moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { DrugProduct } from "@/models/Drug/DrugProduct";
import DateRangeFilter from "../DateRangeFilter.vue";
import BottomPagerBar from "../BottomPagerBar.vue";

@Component({
    name: "InventoryHistory",
    components: { DateRangeFilter, BottomPagerBar },
})
export default class InventoryHistory extends Vue {
    @Prop() protected store?: Store;
    @Prop() protected program?: Program;
    @Prop() protected drug?: DrugProduct;

    protected historyItems: any[] = [];

    private dateRangeFilter: any = { startDate: null, endDate: null };

    protected pageSize = 25;
    protected pageNumber = 1;

    mounted() {
        this.getInventoryHistory();
    }

    @Watch("dateRangeFilter")
    @Watch("pageSize")
    resetPageNumber() {
        this.pageNumber = 1;
    }

    get fields(): BvTableFieldArray {
        return [
            { key: "DrugName" },
            { key: "NDC11" },
            { key: "Updated", formatter: (value: any) => moment.utc(value).local().format("L LTS"), },
            { key: "UpdatedBy" },
            { key: "StoreName", label: "Store", },
            { key: "ProgramName", label: "Program", },
            {
                key: "Reason",
                label: "RxId / Reason",
                formatter: (value: any, _: any, item: any) => item.RxId ? item.RxId : value
            },
            { key: "OldQuantity" },
            { key: "NewQuantity", tdClass: this.cellClass },
        ];
    }

    get inventoryFilter() {
        return {
            storeId: this.store?.id,
            programId: this.program?.id,
            packageId: this.drug?.drugId,
            source: this.drug?.source,
        };
    }

    async getInventoryHistory() {
        try {
            const resp = await Axios.get<any[]>("/api/Inventory/History", { params: this.inventoryFilter });
            const histories = resp.data.map((value, _index, _array) => {
                if (!value.NewValue) return undefined;

                const newValue = JSON.parse(value.NewValue)[0];
                const ret = {
                    ...value,
                    Reason: newValue.Reason,
                    NewQuantity: newValue.Quantity,
                };

                if (value.OldValue) {
                    const oldValue = JSON.parse(value.OldValue)[0];
                    ret.OldQuantity = oldValue.Quantity;
                }
                return ret;
            });

            this.historyItems = histories.filter(h => h);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get filteredHistoryItems() {
        const startDate = this.dateRangeFilter.startDate ? moment.utc(this.dateRangeFilter.startDate).startOf('day') : null;
        const endDate = this.dateRangeFilter.endDate ? moment.utc(this.dateRangeFilter.endDate).endOf('day') : null;

        return this.historyItems.filter(item => {
            const updatedDay = moment.utc(item.Updated).startOf('day');

            if (startDate && updatedDay.isBefore(startDate)) {
                return false;
            }

            if (endDate && updatedDay.isAfter(endDate)) {
                return false;
            }

            return true;
        });
    }

    get displayHistoryItems() {
        const startIndex = this.pageSize * this.pageNumber - this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.filteredHistoryItems.slice(startIndex, endIndex);
    }

    cellClass(value: number, _key: string, item: any) {
        return this.cellClassForQuantities(value, item.OldQuantity);
    }

    cellClassForQuantities(newQuantity: number, oldQuantity: number): string {
        const qtyChange = Number(newQuantity - (oldQuantity ?? 0));
        if (qtyChange > 0) return "table-success";
        if (newQuantity < 0) return "table-danger";
        if (qtyChange < 0) return "table-warning";
        return "";
    }
};
</script>
